import { Link } from "react-router-dom";
import PublicShell, { useSiteContent } from "@/components/layout/PublicShell";
import { ArrowRight, Users, Target } from "lucide-react";

export default function ServicesPage() {
  const content = useSiteContent();
  const services = content?.services?.items || [];
  const title = content?.services?.page_title || "Services";
  const subtitle = content?.services?.page_subtitle;

  return (
    <PublicShell>
      <section className="relative border-b border-border overflow-hidden" data-testid="services-hero">
        <div className="absolute inset-0 bg-dotgrid opacity-60 pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 pt-16 lg:pt-28 pb-16 lg:pb-20">
          <div className="overline mb-4">Services</div>
          <h1 className="font-heading text-5xl lg:text-7xl font-bold tracking-tighter leading-[1] text-obsidian max-w-4xl">
            {title}
          </h1>
          <p className="mt-6 text-lg lg:text-2xl text-muted-foreground max-w-3xl">{subtitle}</p>
        </div>
      </section>

      <section className="border-b border-border" data-testid="services-list">
        {services.map((s, i) => (
          <div
            key={s.key || i}
            className={`border-b border-border ${i % 2 === 1 ? "bg-muted" : "bg-white"}`}
            data-testid={`service-section-${s.key || i}`}
          >
            <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
              <div className={`lg:col-span-4 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <div className="font-mono text-[11px] tracking-[0.3em] text-execution mb-4">
                  {String(i + 1).padStart(2, "0")} / 0{services.length}
                </div>
                <h2 className="font-heading text-3xl lg:text-5xl font-semibold tracking-tight leading-[1.05] text-obsidian">
                  {s.title}
                </h2>
                <p className="mt-6 text-base lg:text-lg text-foreground/80 leading-relaxed">{s.outcome}</p>
              </div>
              <div className={`lg:col-span-8 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                <div className="grid grid-cols-1 md:grid-cols-2 border border-border h-full">
                  <div className={`p-6 lg:p-8 border-b md:border-b-0 md:border-r border-border ${s.staff_aug && s.staff_aug.toLowerCase().startsWith("not applicable") ? "bg-muted opacity-70" : "bg-white"}`}>
                    <div className="flex items-center gap-2 mb-4">
                      <Users className="h-4 w-4 text-execution" />
                      <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-execution">Staff Augmentation</span>
                    </div>
                    <p className="text-sm lg:text-base text-foreground/80 leading-relaxed">{s.staff_aug}</p>
                  </div>
                  <div className="p-6 lg:p-8 bg-obsidian text-white">
                    <div className="flex items-center gap-2 mb-4">
                      <Target className="h-4 w-4 text-execution" />
                      <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-execution">Delivery Services</span>
                    </div>
                    <p className="text-sm lg:text-base text-white/80 leading-relaxed">{s.delivery_services}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </section>

      <section className="bg-obsidian text-white" data-testid="services-cta">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-20 lg:py-28 grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
          <h3 className="md:col-span-8 font-heading text-3xl lg:text-5xl font-bold tracking-tighter leading-[1.02]">
            Choose the model. Standard of delivery is the same.
          </h3>
          <div className="md:col-span-4 flex flex-col sm:flex-row md:justify-end gap-3">
            <Link to="/service-models" className="btn-accent" data-testid="services-models-cta">
              Compare service models
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </PublicShell>
  );
}
